import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast, Toaster } from "sonner";
import {
  BadgeCheck,
  Copy,
  Check,
  ExternalLink,
  ShieldCheck,
  Loader2,
  AlertTriangle,
  Code as CodeIcon,
  Sun,
  Moon,
  Image as ImageIcon,
  Frame,
  FileText,
  Link2,
} from "lucide-react";

function formatKES(value) {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 0,
  }).format(value);
}

const VARIANT_OPTIONS = [
  { code: "pill", label: "Pill", desc: "Compact rounded button — great for footers and bios." },
  { code: "compact", label: "Compact", desc: "Mini badge for tight spaces and email signatures." },
  { code: "card", label: "Card", desc: "Stacked card for catalogues, READMEs, hero rows." },
];

const THEME_OPTIONS = [
  { code: "light", label: "Light", icon: Sun },
  { code: "dark", label: "Dark", icon: Moon },
];

export default function TrustBadge() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");
  const [variant, setVariant] = useState("pill");
  const [theme, setTheme] = useState("light");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/merchant/trust-badge");
      setData(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function copyText(key, text) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(""), 1800);
    } catch {
      toast.error("Couldn't copy — please select and copy manually.");
    }
  }

  const selected = useMemo(() => {
    if (!data?.variants) return null;
    return data.variants[`${variant}_${theme}`] || null;
  }, [data, variant, theme]);

  const previewImg = selected?.image_url;
  const verifyUrl = data?.verify_url;

  return (
    <div
      className="px-5 sm:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8 max-w-5xl mx-auto"
      data-testid="merchant-badge-page"
    >
      <Toaster richColors position="top-right" />

      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#c15337]">
          Trust Badge
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#114232] mt-1.5 flex items-center gap-3">
          <BadgeCheck className="w-7 h-7 sm:w-8 sm:h-8" /> Your verified badge
        </h1>
        <p className="text-sm sm:text-base text-[#5c6360] mt-2 max-w-2xl leading-relaxed">
          Add this badge anywhere you trade — your website, WhatsApp catalogue, Instagram bio,
          POS receipts, READMEs. Anyone who clicks it sees live proof your Fortiqa cover is
          active.
        </p>
      </div>

      {loading ? (
        <div
          className="rounded-2xl bg-white border border-[#114232]/10 p-12 grid place-items-center text-[#5c6360]"
          data-testid="badge-loading"
        >
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : error ? (
        <div
          className="rounded-2xl bg-[#c15337]/8 border border-[#c15337]/25 p-5"
          data-testid="badge-error"
        >
          <strong>{error}</strong>
          <button
            onClick={load}
            data-testid="badge-retry"
            className="ml-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-[#114232]/20 rounded-full bg-white hover:bg-[#114232]/5 text-[#114232] transition-colors"
          >
            Retry
          </button>
        </div>
      ) : !data.verified ? (
        <div
          className="rounded-2xl bg-[#d49a36]/8 border border-[#d49a36]/30 p-7 flex items-start gap-4"
          data-testid="badge-not-verified"
        >
          <AlertTriangle className="w-6 h-6 text-[#8a6520] shrink-0 mt-0.5" strokeWidth={2} />
          <div>
            <div className="font-heading text-lg font-bold text-[#114232]">
              Your badge activates when cover does
            </div>
            <div className="text-sm text-[#5c6360] mt-1.5 max-w-lg">
              Once your policy moves out of <code>pending</code> into{" "}
              <code>cooling_off</code> or <code>active</code>, your verifier URL goes live and
              the badge auto-issues. Complete payment to activate.
            </div>
            <Link
              to="/merchant/cover-options"
              className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-full bg-[#114232] hover:bg-[#0d3125] text-[#f4f2eb] text-xs font-semibold transition-colors"
            >
              Activate cover
            </Link>
          </div>
        </div>
      ) : (
        <>
          {/* Generator: variant + theme + live preview */}
          <div
            className="rounded-3xl bg-white border border-[#114232]/10 p-6 sm:p-8 shadow-md"
            data-testid="badge-generator"
          >
            <div className="grid lg:grid-cols-[1fr_320px] gap-7 items-start">
              <div className="space-y-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#c15337] font-bold mb-2">
                    Style
                  </div>
                  <div className="grid sm:grid-cols-3 gap-2.5">
                    {VARIANT_OPTIONS.map((v) => (
                      <button
                        key={v.code}
                        type="button"
                        onClick={() => setVariant(v.code)}
                        data-testid={`badge-variant-${v.code}`}
                        className={`text-left rounded-xl border p-3.5 transition-all ${
                          variant === v.code
                            ? "border-[#114232] bg-[#114232]/5 ring-1 ring-[#114232]/20"
                            : "border-[#114232]/12 hover:border-[#114232]/30"
                        }`}
                      >
                        <div className="font-heading font-bold text-sm text-[#114232]">
                          {v.label}
                        </div>
                        <div className="text-[11px] text-[#5c6360] mt-0.5 leading-snug">
                          {v.desc}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#c15337] font-bold mb-2">
                    Theme
                  </div>
                  <div className="inline-flex rounded-full bg-[#114232]/5 p-1">
                    {THEME_OPTIONS.map(({ code, label, icon: Icon }) => (
                      <button
                        key={code}
                        type="button"
                        onClick={() => setTheme(code)}
                        data-testid={`badge-theme-${code}`}
                        className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                          theme === code
                            ? "bg-[#114232] text-[#f4f2eb]"
                            : "text-[#5c6360] hover:text-[#114232]"
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" /> {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-3 pt-1">
                  {[
                    ["Tier", data.tier],
                    ["Cover limit", formatKES(data.cover_limit_kes)],
                    ["Status", data.status === "active" ? "Active" : "Cooling-off"],
                  ].map(([l, v]) => (
                    <div key={l} className="rounded-xl bg-[#f4f2eb] border border-[#114232]/8 p-3.5">
                      <div className="text-[10px] uppercase tracking-wider text-[#5c6360] font-bold">
                        {l}
                      </div>
                      <div className="font-heading font-bold text-base text-[#114232] mt-0.5 metric-value">
                        {v}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Live preview */}
              <div
                className={`rounded-2xl p-6 grid place-items-center min-h-[180px] transition-colors ${
                  theme === "dark" ? "bg-[#0d3125]" : "bg-[#f4f2eb]"
                }`}
                data-testid="badge-preview-pane"
              >
                {previewImg ? (
                  <a
                    href={verifyUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="badge-preview-link"
                    className="no-underline"
                  >
                    <img
                      src={previewImg}
                      alt={`Fortiqa Verified · ${data.handle}`}
                      data-testid="badge-preview-img"
                    />
                  </a>
                ) : (
                  <span className="text-xs text-[#5c6360]">Pick a style</span>
                )}
                <a
                  href={verifyUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={`mt-4 inline-flex items-center gap-1.5 text-[11px] font-semibold transition-colors ${
                    theme === "dark"
                      ? "text-[#d49a36] hover:text-[#e8b04a]"
                      : "text-[#c15337] hover:text-[#a8472f]"
                  }`}
                  data-testid="badge-open-verifier"
                >
                  Open public verifier <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>

          {/* Snippet rows */}
          {selected && (
            <div className="space-y-4">
              <Snippet
                label="Image tag (recommended)"
                sublabel="Drop into HTML, email templates, or anywhere <img> works."
                icon={ImageIcon}
                value={selected.image_tag}
                onCopy={() => copyText("img", selected.image_tag)}
                copied={copied === "img"}
                multiline
                testid="snippet-img"
              />
              {variant === "pill" && selected.html && (
                <Snippet
                  label="Inline HTML pill"
                  sublabel="Self-contained — no external image. Best for sites with image-blocked emails."
                  icon={CodeIcon}
                  value={selected.html}
                  onCopy={() => copyText("html", selected.html)}
                  copied={copied === "html"}
                  multiline
                  testid="snippet-html"
                />
              )}
              <Snippet
                label="Markdown"
                sublabel="For READMEs, Linktree, Notion, GitHub issues."
                icon={FileText}
                value={selected.markdown}
                onCopy={() => copyText("md", selected.markdown)}
                copied={copied === "md"}
                multiline
                testid="snippet-md"
              />
              <Snippet
                label="iFrame embed"
                sublabel="Embed the live verifier card inside any page."
                icon={Frame}
                value={data.embed_iframe}
                onCopy={() => copyText("iframe", data.embed_iframe)}
                copied={copied === "iframe"}
                multiline
                testid="snippet-iframe"
              />
              <Snippet
                label="Image URL"
                sublabel="Direct link to the live SVG — paste in WhatsApp catalogue images, social DMs."
                icon={Link2}
                value={selected.image_url}
                onCopy={() => copyText("imgurl", selected.image_url)}
                copied={copied === "imgurl"}
                testid="snippet-img-url"
              />
              <Snippet
                label="Verify URL"
                sublabel="Share anywhere you want a click-to-verify."
                icon={ShieldCheck}
                value={data.verify_url}
                onCopy={() => copyText("url", data.verify_url)}
                copied={copied === "url"}
                testid="snippet-url"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Snippet({ label, sublabel, icon: Icon, value, onCopy, copied, multiline, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-[#114232]/10 p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-start gap-3">
          {Icon && (
            <div className="w-9 h-9 rounded-xl bg-[#114232]/8 grid place-items-center shrink-0">
              <Icon className="w-4 h-4 text-[#114232]" strokeWidth={1.7} />
            </div>
          )}
          <div>
            <div className="font-heading text-sm font-bold text-[#114232]">{label}</div>
            <div className="text-xs text-[#5c6360] mt-0.5">{sublabel}</div>
          </div>
        </div>
        <button
          onClick={onCopy}
          data-testid={`${testid}-copy`}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all shrink-0 ${
            copied ? "bg-[#2d7a4a] text-white" : "bg-[#114232] hover:bg-[#0d3125] text-[#f4f2eb]"
          }`}
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5" /> Copied
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" /> Copy
            </>
          )}
        </button>
      </div>
      <code
        className={`block bg-[#f4f2eb] border border-[#114232]/8 rounded-xl px-4 py-3 text-xs text-[#1b1d1c] font-mono ${
          multiline ? "whitespace-pre-wrap break-all" : "truncate"
        }`}
      >
        {value}
      </code>
    </div>
  );
}
